// src/pages/TicketDesignTemplate/components/TicketLayoutEditor.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Rnd } from 'react-rnd';
import { Button, Spinner } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_design_template');

type TicketElementType = 'ticketCode' | 'ticketNumber' | 'qr' | 'drawDate';

interface TicketLayoutElement {
  id: TicketElementType;
  // Toạ độ/kích thước tính theo % so với ảnh nền để không phụ thuộc kích thước hiển thị thực tế
  x: number;
  y: number;
  width: number;
  height: number;
}

const ELEMENT_META: Record<TicketElementType, { label: string; icon: string }> = {
  ticketCode: { label: st('ma_ve_label'), icon: 'ri-price-tag-3-line' },
  ticketNumber: { label: st('so_ve_bo_so_label'), icon: 'ri-hashtag' },
  drawDate: { label: st('ngay_mo_thuong_label'), icon: 'ri-calendar-event-line' },
  qr: { label: st('ma_qr_label'), icon: 'ri-qr-code-line' },
};

const DEFAULT_ELEMENTS: TicketLayoutElement[] = [
  { id: 'ticketCode', x: 4, y: 4, width: 32, height: 9 },
  { id: 'ticketNumber', x: 4, y: 15, width: 32, height: 9 },
  { id: 'drawDate', x: 4, y: 26, width: 32, height: 9 },
  { id: 'qr', x: 70, y: 4, width: 24, height: 24 },
];

// Parse layout đã lưu — chỉ trả về ĐÚNG các phần tử đã lưu (cho phép chèn thêm/xóa bớt thực sự,
// phần tử đã xóa thì không tự động hiện lại). Mẫu vé chưa từng lưu bố cục lần nào (layoutJson
// rỗng) mới dùng bộ mặc định 4 phần tử làm điểm khởi đầu thân thiện.
const parseLayoutJson = (layoutJson?: string | null): TicketLayoutElement[] => {
  if (!layoutJson) return DEFAULT_ELEMENTS;

  try {
    const parsed = JSON.parse(layoutJson);
    if (Array.isArray(parsed)) {
      return parsed.filter((item): item is TicketLayoutElement =>
        item && typeof item.id === 'string' && item.id in ELEMENT_META);
    }
  } catch {
    // layoutJson hỏng — fallback ve mac dinh
  }

  return DEFAULT_ELEMENTS;
};

interface TicketLayoutEditorProps {
  imageUrl: string | null;
  loading?: boolean;
  emptyText?: string;
  initialLayoutJson?: string | null;
  onLayoutChange?: (layoutJson: string) => void;
  // Chỉ hiển thị bố cục đã lưu, không cho kéo-thả/chỉnh sửa — dùng cho màn Xem trước
  readOnly?: boolean;
}

const TicketLayoutEditor: React.FC<TicketLayoutEditorProps> = ({
  imageUrl,
  loading = false,
  emptyText = st('chua_co_anh_mau_ve_text'),
  initialLayoutJson,
  onLayoutChange,
  readOnly = false,
}) => {
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const initialElements = useMemo(() => parseLayoutJson(initialLayoutJson), [imageUrl, initialLayoutJson]);
  const [elements, setElements] = useState<TicketLayoutElement[]>(initialElements);
  const [selectedId, setSelectedId] = useState<TicketElementType | null>(null);

  // Nạp lại layout mỗi khi đổi ảnh mẫu vé hoặc layout ban đầu thay đổi
  useEffect(() => {
    setElements(initialElements);
    setSelectedId(null);
  }, [initialElements]);

  useEffect(() => {
    const containerEl = containerRef.current;
    if (!containerEl) return;

    const updateSize = () => {
      setContainerSize({ width: containerEl.clientWidth, height: containerEl.clientHeight });
    };
    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(containerEl);
    return () => observer.disconnect();
  }, [imageUrl]);

  const applyElements = (next: TicketLayoutElement[]) => {
    setElements(next);
    onLayoutChange?.(JSON.stringify(next));
  };

  const handleReset = () => {
    applyElements(DEFAULT_ELEMENTS);
    setSelectedId(null);
  };

  // Cac loai phan tu chua duoc dat tren mau — hien thanh nut "Chen them" o sidebar
  const availableTypes = (Object.keys(ELEMENT_META) as TicketElementType[])
    .filter(id => !elements.some(el => el.id === id));

  const handleAddElement = (id: TicketElementType) => {
    if (elements.some(el => el.id === id)) return;
    const fallback = DEFAULT_ELEMENTS.find(def => def.id === id) ?? { id, x: 10, y: 10, width: 32, height: 9 };
    applyElements([...elements, fallback]);
    setSelectedId(id);
  };

  const handleRemoveElement = (id: TicketElementType) => {
    applyElements(elements.filter(el => el.id !== id));
    setSelectedId(prev => (prev === id ? null : prev));
  };

  const toPx = (percent: number, base: number) => (percent / 100) * base;
  const toPercent = (px: number, base: number) => (base > 0 ? (px / base) * 100 : 0);

  const canvas = (
    <div
      ref={containerRef}
      className="position-relative border rounded bg-light overflow-hidden mx-auto"
      style={{ width: '100%', aspectRatio: '3 / 2', maxHeight: '65vh' }}
    >
      {loading ? (
        <div className="d-flex align-items-center justify-content-center h-100">
          <Spinner color="primary" />
        </div>
      ) : imageUrl ? (
        <img
          src={imageUrl}
          alt={st('mau_ve_alt')}
          draggable={false}
          style={{ width: '100%', height: '100%', objectFit: 'contain', userSelect: 'none' }}
        />
      ) : (
        <div className="d-flex align-items-center justify-content-center h-100 text-muted">
          {emptyText}
        </div>
      )}

      {!loading && imageUrl && containerSize.width > 0 && elements.map(el => (
        <Rnd
          key={el.id}
          bounds="parent"
          disableDragging={readOnly}
          enableResizing={!readOnly}
          size={{
            width: toPx(el.width, containerSize.width),
            height: toPx(el.height, containerSize.height),
          }}
          position={{
            x: toPx(el.x, containerSize.width),
            y: toPx(el.y, containerSize.height),
          }}
          minWidth={32}
          minHeight={24}
          onDragStart={() => !readOnly && setSelectedId(el.id)}
          onDragStop={(_event, data) => {
            if (readOnly) return;
            applyElements(elements.map(item => (item.id === el.id
              ? {
                  ...item,
                  x: toPercent(data.x, containerSize.width),
                  y: toPercent(data.y, containerSize.height),
                }
              : item)));
          }}
          onResizeStart={() => !readOnly && setSelectedId(el.id)}
          onResizeStop={(_event, _direction, ref, _delta, position) => {
            if (readOnly) return;
            applyElements(elements.map(item => (item.id === el.id
              ? {
                  ...item,
                  width: toPercent(ref.offsetWidth, containerSize.width),
                  height: toPercent(ref.offsetHeight, containerSize.height),
                  x: toPercent(position.x, containerSize.width),
                  y: toPercent(position.y, containerSize.height),
                }
              : item)));
          }}
        >
          <div
            className={`d-flex align-items-center justify-content-center h-100 w-100 border border-2 rounded ${!readOnly && selectedId === el.id ? 'border-primary' : 'border-danger'}`}
            style={{
              background: 'transparent',
              cursor: readOnly ? 'default' : 'move',
              overflow: 'hidden',
              padding: 2,
            }}
            onClick={() => !readOnly && setSelectedId(el.id)}
            title={ELEMENT_META[el.id].label}
          >
            <i className={`${ELEMENT_META[el.id].icon} me-1`}></i>
            <span className="small fw-semibold text-truncate">{ELEMENT_META[el.id].label}</span>
          </div>
        </Rnd>
      ))}
    </div>
  );

  if (readOnly) {
    return (
      <div>
        {imageUrl && (
          <div className="alert alert-info py-2 mb-3">
            <i className="ri-layout-2-line me-1"></i>
            {st('bo_cuc_ma_ve_so_ve_text')}
          </div>
        )}
        {canvas}
      </div>
    );
  }

  return (
    <div>
      <div className="alert alert-info py-2 mb-3">
        <i className="ri-drag-move-2-line me-1"></i>
        {st('keo_de_di_chuyen_keo_goc_text')}
      </div>

      <div className="row g-3">
        <div className="col-lg-9">
          {canvas}
        </div>

        <div className="col-lg-3">
          <h6 className="mb-2">{st('cac_thong_tin_da_chen_tren_title')}</h6>
          {elements.length === 0 && (
            <div className="text-muted small mb-2">{st('chua_co_thong_tin_nao_bam_text')}</div>
          )}
          <ul className="list-group">
            {elements.map(el => (
              <li
                key={el.id}
                className={`list-group-item d-flex align-items-center gap-2 ${selectedId === el.id ? 'active' : ''}`}
                style={{ cursor: imageUrl ? 'pointer' : 'default' }}
                onClick={() => imageUrl && setSelectedId(el.id)}
              >
                <i className={ELEMENT_META[el.id].icon}></i>
                <div className="flex-grow-1 min-w-0">
                  <div className="fw-semibold text-truncate">{ELEMENT_META[el.id].label}</div>
                </div>
                {PageActionPermission.canDisplay(location.pathname, "button-remove-layout-element") && (
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-danger p-0 flex-shrink-0"
                    title={st('xoa_label_khoi_mau_text', { label: ELEMENT_META[el.id].label })}
                    data-action-code="button-remove-layout-element"
                    data-action-name="Xóa thông tin khỏi mẫu vé"
                    disabled={!imageUrl}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveElement(el.id);
                    }}
                  >
                    <i className="ri-close-line"></i>
                  </button>
                )}
              </li>
            ))}
          </ul>

          {availableTypes.length > 0 && PageActionPermission.canDisplay(location.pathname, "button-add-layout-element") && (
            <div className="mt-3">
              <div className="small text-muted mb-2">{st('chen_them_vao_mau_text')}</div>
              <div className="d-flex flex-wrap gap-2">
                {availableTypes.map(id => (
                  <Button
                    key={id}
                    color="primary"
                    outline
                    size="sm"
                    disabled={!imageUrl}
                    data-action-code="button-add-layout-element"
                    data-action-name="Chèn thông tin vào mẫu vé"
                    onClick={() => handleAddElement(id)}
                  >
                    <i className={`${ELEMENT_META[id].icon} me-1`}></i>
                    {ELEMENT_META[id].label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {PageActionPermission.canDisplay(location.pathname, "button-reset-layout") && (
            <Button
              color="light"
              outline
              size="sm"
              className="w-100 mt-3"
              disabled={!imageUrl}
              data-action-code="button-reset-layout"
              data-action-name="Đặt lại vị trí mặc định"
              onClick={handleReset}
            >
              <i className="ri-refresh-line me-1"></i>
              {st('dat_lai_vi_tri_mac_dinh_button')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketLayoutEditor;
